import EditIcon from "@mui/icons-material/Edit"
import { Box, CircularProgress, Grid, MenuItem } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { useFormik } from "formik"
import { useAppDispatch, useAppSelector } from "hooks"
import { useState } from "react"
import licensePoolService from "services/licensePoolService"
import licenseService from "services/licenseService"
import { updateLicensePool } from "slices/licensePoolSlice"
import { updateLicense } from "slices/licenseSlice"
import type { ILicense, IUpdateLicense } from "types/licenseTypes"
import * as Yup from "yup"

type Props = {
  license: ILicense
}

export function UpdateLicenseDialog({ license }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)

  const currentLicensePool = useAppSelector((state) => state.licensepool.currentLicensePool)
  const users = useAppSelector((state) => state.users.users)
  const dispatch = useAppDispatch()

  const initialValues: IUpdateLicense = {
    assignedUserId: license.assignedUserId,
    cancelationDate: license.cancelationDate,
    licenseCanceled: license.licenseCanceled,
  }

  const updateLicenseValidationScheme = Yup.object({
    assignedUserId: Yup.string().required("Please enter user"),
    cancelationDate: Yup.date().required("Please enter cancelation date"),
    licenseCanceled: Yup.boolean().required("Please enter cancelation state"),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: updateLicenseValidationScheme,
    onSubmit: (_values) => {
      if (currentLicensePool === undefined) {
        return
      }

      setIsLoading(true)

      const licenseUpdate: IUpdateLicense = {
        assignedUserId: formik.values.assignedUserId,
        cancelationDate: formik.values.cancelationDate,
        licenseCanceled: formik.values.licenseCanceled,
      }

      licenseService
        .updateLicense(license.id, licenseUpdate)
        .then((license) => {
          dispatch(updateLicense(license))

          licensePoolService
            .getLicensePool(currentLicensePool.id)
            .then((licensePool) => {
              dispatch(updateLicensePool(licensePool))
              setIsLoading(false)
              handleClose()
            })
            .catch((err) => {
              console.log(err)
              setIsLoading(false)
            })
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <MenuItem onClick={handleClickOpen}>
        <EditIcon sx={{ marginRight: 1 }} />
        Update
      </MenuItem>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Edit license</DialogTitle>
          <DialogContent>
            <Grid container>
              <Grid item xs={12}>
                <TextField
                  label="Assigned to user"
                  disabled
                  fullWidth
                  margin="dense"
                  value={`${users.find((x) => x.id === initialValues.assignedUserId)?.firstname} ${
                    users.find((x) => x.id === initialValues.assignedUserId)?.lastname
                  }`}
                ></TextField>
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DesktopDatePicker
                    label="Cancelation date"
                    inputFormat="DD/MM/YYYY"
                    value={formik.values.cancelationDate}
                    onChange={(date) => formik.setFieldValue("cancelationDate", date)}
                    renderInput={(params) => (
                      <TextField fullWidth required margin="dense" {...params} />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="License canceled"
                  disabled
                  fullWidth
                  margin="dense"
                  value={formik.values.licenseCanceled}
                ></TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Update
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
